/**
 * Which files of a song the viewer offers, in which order, and which one opens by default
 * (pure — unit tested).
 */
import { audienceSpecificity, matchesOwnAudience, roleAtLeast, type Role, type Section } from '../../shared/audience';
import type { FileKind, SongFileDTO } from '../../shared/dto/library';

export interface Viewer {
  role: Role;
  section: Section | null;
}

const collator = new Intl.Collator('is', { sensitivity: 'base', numeric: true });

/** Own section first, then whole choir, then the rest; then `sort`, then title. */
export function compareForUser(section: Section | null) {
  return (a: SongFileDTO, b: SongFileDTO): number =>
    audienceSpecificity(b.audienceMask, section) - audienceSpecificity(a.audienceMask, section) ||
    a.sort - b.sort ||
    collator.compare(a.title, b.title) ||
    a.id.localeCompare(b.id);
}

/**
 * The one editor rule (viewer default view AND offline packs without "allar raddir", offline/plan.ts):
 * of one kind, the files aimed at the editor's own audience — or every file of that kind when none
 * is, so a song whose sheets / tracks are all per-voice is still readable / playable (offline too).
 */
export function ownAudienceOrAll<T extends { kind: FileKind; audienceMask: number }>(files: readonly T[], kind: FileKind, section: Section | null): T[] {
  const ofKind = files.filter((f) => f.kind === kind);
  const own = ofKind.filter((f) => matchesOwnAudience(f.audienceMask, section));
  return own.length > 0 ? own : ofKind;
}

/**
 * Files of one kind the viewer shows. Members only ever receive their own audience's files from the
 * server. Editors receive everything: by default they see the files aimed at their own audience
 * (falling back to all when none is: ownAudienceOrAll), and with "Allar skrár" every file, own
 * audience first. `usable` (e.g. isViewableSheet) is applied BEFORE the own-audience choice, so an
 * own-section file that is still uploading (rev 0) does not hide the ready files of other sections.
 */
export function filesForViewer(
  files: readonly SongFileDTO[],
  kind: FileKind,
  viewer: Viewer,
  allFiles: boolean,
  usable?: (f: SongFileDTO) => boolean,
): SongFileDTO[] {
  const ofKind = files.filter((f) => f.kind === kind && (!usable || usable(f))).sort(compareForUser(viewer.section));
  if (!roleAtLeast(viewer.role, 'editor') || allFiles) return ofKind;
  return ownAudienceOrAll(ofKind, kind, viewer.section);
}

/** Can the editor's "Allar skrár" toggle reveal anything more (among usable files) for this kind? */
export function hasHiddenFiles(files: readonly SongFileDTO[], viewer: Viewer): boolean {
  if (!roleAtLeast(viewer.role, 'editor')) return false;
  return (['sheet', 'audio', 'lyrics'] as const).some((k) => {
    const usable = USABLE[k];
    return filesForViewer(files, k, viewer, false, usable).length < filesForViewer(files, k, viewer, true, usable).length;
  });
}

/**
 * Default file: highest audience specificity for the user's section, then `sort`, then the file
 * last used on this device for this song.
 */
export function pickDefaultFile(files: readonly SongFileDTO[], section: Section | null, lastUsedId: string | null): SongFileDTO | null {
  if (files.length === 0) return null;
  const sorted = [...files].sort(
    (a, b) =>
      audienceSpecificity(b.audienceMask, section) - audienceSpecificity(a.audienceMask, section) ||
      a.sort - b.sort ||
      (b.id === lastUsedId ? 1 : 0) - (a.id === lastUsedId ? 1 : 0) ||
      collator.compare(a.title, b.title),
  );
  return sorted[0]!;
}

/** A sheet is viewable when its page list and at least the 's' tier exist. */
export function isViewableSheet(f: SongFileDTO): boolean {
  return f.kind === 'sheet' && f.rev > 0 && !!f.pages && f.pages.length > 0 && !!f.tiers?.s;
}

/** An audio file is playable once its first revision was uploaded. */
export function isPlayableAudio(f: SongFileDTO): boolean {
  return f.kind === 'audio' && f.rev > 0;
}

/** What the song page can actually show, per kind (lyrics are inline text: always usable). */
export const USABLE: Record<FileKind, ((f: SongFileDTO) => boolean) | undefined> = {
  sheet: isViewableSheet,
  audio: isPlayableAudio,
  lyrics: undefined,
};
